import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import store from '../../redux/store';
import { handleErrorResponse } from '../../utils/handleError';
import apiClient from '../../lib/apiClient';
import { triggerSnackbar } from '../../provider/SnackbarProvider';
import { useAppDispatch } from '../../redux/hooks';
import { clearAuthData } from '../../redux/slices/authSlice';

export interface PutResetPasswordVariables {
  token: string;
  password: string;
  confirmPassword: string;
}

export interface PutResetPasswordResponseData {
  message: string;
}

export interface PutSetPasswordResponseData {
  message: string;
}

export interface PutSetPasswordVariables {
  password: string;
  confirmPassword: string;
}

export async function putResetPassword(variables: PutResetPasswordVariables) {
  const { password, confirmPassword, token } = variables;

  const response = await apiClient.put<PutResetPasswordResponseData>(
    '/patients/reset_password',
    {
      patient: { password, confirm_password: confirmPassword, token },
    }
  );

  return response.data;
}

export async function putSetPassword(variables: PutSetPasswordVariables) {
  const { password, confirmPassword } = variables;
  const state = store.getState();
  const token = state.auth.accessToken;

  const response = await apiClient.put<PutSetPasswordResponseData>(
    '/patients/set_password',
    { patient: { password, confirm_password: confirmPassword } },
    {
      headers: {
        Authorization: token,
      },
    }
  );

  return response.data;
}

const clearLocalStorage = () => {
  localStorage.removeItem('AUTH_USER_DATA');
};

export function useCreatePassword() {
  const navigate = useNavigate();
  const [disableSubmit, setDisableSubmit] = useState(false);
  const dispatch = useAppDispatch();

  const { mutate: mutateResetPassword, isPending: isResetPasswordPending } =
    useMutation({
      mutationKey: ['patient-reset-password'],
      mutationFn: putResetPassword,
      onSuccess: (data) => {
        const message = data?.message;
        if (message)
          triggerSnackbar({
            message: data?.message,
            severity: 'success',
          });

        dispatch(clearAuthData());

        clearLocalStorage();
        navigate('/setup/email', { replace: true });
      },
      onError: (error) => handleErrorResponse(error, 'Reset password'),
      onSettled: () => setDisableSubmit(false),
    });

  const { mutate: mutateSetPassword, isPending: isSetPasswordPending } =
    useMutation({
      mutationKey: ['patient-set-password'],
      mutationFn: putSetPassword,
      onSuccess: (data) => {
        const message = data?.message;
        if (message)
          triggerSnackbar({
            message: data?.message,
            severity: 'success',
          });
        dispatch(clearAuthData());
        clearLocalStorage();
        navigate('/setup/email', { replace: true });
      },
      onError: (error) => handleErrorResponse(error, 'Create password'),
      onSettled: () => setDisableSubmit(false),
    });

  const sendResetPasswordRequest = (token: string, password: string) => {
    setDisableSubmit(true);
    mutateResetPassword({
      token,
      password: password,
      confirmPassword: password,
    });
  };

  const sendSetPasswordRequest = (password: string) => {
    setDisableSubmit(true);
    mutateSetPassword({ password, confirmPassword: password });
  };

  return {
    sendResetPasswordRequest,
    sendSetPasswordRequest,
    disableSubmit:
      isResetPasswordPending || isSetPasswordPending || disableSubmit,
    isPending: isResetPasswordPending || isSetPasswordPending,
  };
}
